export const BUILDING_CATEGORIES = {
  CIVIL: 'Civil Buildings',
  MILITARY: 'Military Buildings',
  MYSTIC: 'Mystic & Thievery Buildings',
} as const;

export type BuildingGroup = 'economy' | 'military' | 'magic' | 'science';

export const BUILDINGS = {
  CIVIL: [
    { id: 'HOME', name: 'Homes', group: 'economy' as BuildingGroup },
    { id: 'FARM', name: 'Farms', group: 'economy' as BuildingGroup },
    { id: 'MILL', name: 'Mills', group: 'economy' as BuildingGroup },
    { id: 'BANK', name: 'Banks', group: 'economy' as BuildingGroup },
    { id: 'LIBRARY', name: 'Libraries', group: 'science' as BuildingGroup },
    { id: 'SCHOOL', name: 'Schools', group: 'science' as BuildingGroup },
  ],
  MILITARY: [
    { id: 'TRAINING_GROUND', name: 'Training Grounds', group: 'military' as BuildingGroup },
    { id: 'ARMOURY', name: 'Armouries', group: 'military' as BuildingGroup },
    { id: 'MILITARY_BARRACK', name: 'Barracks', group: 'military' as BuildingGroup },
    { id: 'FORT', name: 'Forts', group: 'military' as BuildingGroup },
    { id: 'GUARD_STATION', name: 'Guard Stations', group: 'military' as BuildingGroup },
    { id: 'HOSPITAL', name: 'Hospitals', group: 'military' as BuildingGroup },
    { id: 'STABLE', name: 'Stables', group: 'military' as BuildingGroup },
    { id: 'DUNGEON', name: 'Dungeons', group: 'military' as BuildingGroup },
  ],
  MYSTIC: [
    { id: 'GUILD', name: 'Guilds', group: 'magic' as BuildingGroup },
    { id: 'TOWER', name: 'Towers', group: 'magic' as BuildingGroup },
    { id: 'THIEVES_DEN', name: "Thieves' Dens", group: 'magic' as BuildingGroup },
    { id: 'WATCH_TOWER', name: 'Watchtowers', group: 'magic' as BuildingGroup },
  ],
} as const;

export interface BuildingDefinition {
  id: string;
  name: string;
  group: BuildingGroup;
}

/** Ordered groups for the redesigned Buildings page (SF-69). */
export const BUILDING_GROUPS: { key: BuildingGroup; label: string; buildings: BuildingDefinition[] }[] = [
  {
    key: 'economy',
    label: 'Economy',
    buildings: [
      { id: 'HOME', name: 'Homes', group: 'economy' },
      { id: 'FARM', name: 'Farms', group: 'economy' },
      { id: 'MILL', name: 'Mills', group: 'economy' },
      { id: 'BANK', name: 'Banks', group: 'economy' },
    ],
  },
  {
    key: 'military',
    label: 'Military',
    buildings: [
      { id: 'TRAINING_GROUND', name: 'Training Grounds', group: 'military' },
      { id: 'ARMOURY', name: 'Armouries', group: 'military' },
      { id: 'MILITARY_BARRACK', name: 'Barracks', group: 'military' },
      { id: 'FORT', name: 'Forts', group: 'military' },
      { id: 'GUARD_STATION', name: 'Guard Stations', group: 'military' },
      { id: 'STABLE', name: 'Stables', group: 'military' },
      { id: 'HOSPITAL', name: 'Hospitals', group: 'military' },
      { id: 'DUNGEON', name: 'Dungeons', group: 'military' },
    ],
  },
  {
    key: 'magic',
    label: 'Magic & Thievery',
    buildings: [
      { id: 'THIEVES_DEN', name: "Thieves' Dens", group: 'magic' },
      { id: 'WATCH_TOWER', name: 'Watchtowers', group: 'magic' },
      { id: 'GUILD', name: 'Guilds', group: 'magic' },
      { id: 'TOWER', name: 'Towers', group: 'magic' },
    ],
  },
  {
    key: 'science',
    label: 'Science',
    buildings: [
      { id: 'SCHOOL', name: 'Schools', group: 'science' },
      { id: 'LIBRARY', name: 'Libraries', group: 'science' },
    ],
  },
];

// Building pairs for classic Utopia two-column layout (matching original game order)
export const BUILDING_PAIRS: readonly [string, string][] = [
  ['HOME', 'FARM'],
  ['MILL', 'BANK'],
  ['TRAINING_GROUND', 'ARMOURY'],
  ['MILITARY_BARRACK', 'FORT'],
  ['GUARD_STATION', 'HOSPITAL'],
  ['GUILD', 'TOWER'],
  ['THIEVES_DEN', 'WATCH_TOWER'],
  ['LIBRARY', 'SCHOOL'],
  ['STABLE', 'DUNGEON'],
] as const;

// Flat building lookup for efficient access
export const BUILDING_MAP: Record<string, BuildingDefinition> = {
  ...Object.fromEntries(BUILDINGS.CIVIL.map((b) => [b.id, b])),
  ...Object.fromEntries(BUILDINGS.MILITARY.map((b) => [b.id, b])),
  ...Object.fromEntries(BUILDINGS.MYSTIC.map((b) => [b.id, b])),
};

// Building order matching classic Utopia (used in Internal Affairs and Survey results)
export const BUILDING_ORDER = [
  'NONE', // Barren Lands
  'HOME',
  'FARM',
  'MILL',
  'BANK',
  'TRAINING_GROUND',
  'ARMOURY',
  'MILITARY_BARRACK',
  'FORT',
  'GUILD',
  'TOWER',
  'THIEVES_DEN',
  'WATCH_TOWER',
  'SCHOOL',
  'LIBRARY',
  'HOSPITAL',
  'GUARD_STATION',
  'STABLE',
  'DUNGEON',
] as const;

export type BuildingCategory = keyof typeof BUILDINGS;
export type BuildingId = (typeof BUILDINGS)[BuildingCategory][number]['id'];
